import {
  Activity,
  BookOpen,
  BookOpenCheck,
  Bot,
  CalendarCheck,
  CircleCheck,
  CircleDashed,
  CircleX,
  ClipboardList,
  Clock,
  Coins,
  ContactRound,
  FileText,
  HandCoins,
  HandHelping,
  HeartPulse,
  History,
  Link2,
  ListChecks,
  ListTodo,
  LoaderCircle,
  LucideIcon,
  MessageSquareText,
  MessagesSquare,
  Receipt,
  RefreshCw,
  Sparkles,
  StickyNote,
  TriangleAlert,
  UserRoundSearch,
  Wallet,
} from 'lucide-react';

import type {
  AiEvidenceKind,
  AiPortraitList,
  AiPortraitStatus,
  AiPortraitTextSection,
} from 'core/hooks/AiPortrait/aiPortraitModel';

/**
 * ТЗ 0104, агент 3 — значки ИИ-портрета студента. Одна карта на смысл;
 * полноту держит `__tests__/aiPortraitIcons.test.ts`.
 *
 * Значки сущностей — те же, что у вкладок карточки студента
 * (`StudentTabBar`) и видов связи задачи (`taskLinkKinds`): переписка —
 * `MessagesSquare`, SMS — `MessageSquareText`, задачи — `ListChecks`, опросы
 * — `ClipboardList`, договор — `FileText`, платежи — `Wallet`, счета —
 * `Receipt`, заявка дофинансирования — `UserRoundSearch`.
 */

/** Портрет — тот же знак, что у агента `student_portrait` на `/ai-agent`. */
export const AI_PORTRAIT_ICON: LucideIcon = ContactRound;
/** «AI — wersja robocza». */
export const AI_PORTRAIT_DRAFT_ICON: LucideIcon = Bot;
export const AI_PORTRAIT_REFRESH_ICON: LucideIcon = RefreshCw;
export const AI_PORTRAIT_SUMMARY_ICON: LucideIcon = Sparkles;
/** «Są nowe wydarzenia od ostatniego portretu». */
export const AI_PORTRAIT_STALE_ICON: LucideIcon = History;
/** Финансовое приложение. */
export const AI_PORTRAIT_FINANCE_ICON: LucideIcon = HandCoins;
/** Пункты финансового приложения (деньги, договоры, дофинансирование). */
export const AI_PORTRAIT_FINANCE_ITEMS_ICON: LucideIcon = Coins;

/** Статус портрета → значок метки (как у статусов прогона на `/ai-agent`). */
export const AI_PORTRAIT_STATUS_ICONS: Readonly<
  Record<AiPortraitStatus, LucideIcon>
> = {
  none: CircleDashed,
  queued: Clock,
  running: LoaderCircle,
  ready: CircleCheck,
  failed: CircleX,
};

/** Текстовый раздел → значок заголовка. */
export const AI_PORTRAIT_SECTION_ICONS: Readonly<
  Record<AiPortraitTextSection, LucideIcon>
> = {
  learning: BookOpen,
  engagement: Activity,
  sentimentTrend: HeartPulse,
  communication: MessagesSquare,
};

/** Список → значок заголовка. */
export const AI_PORTRAIT_LIST_ICONS: Readonly<
  Record<AiPortraitList, LucideIcon>
> = {
  risks: TriangleAlert,
  openNeeds: HandHelping,
  nextActions: ListTodo,
};

/** Вид доказательства → значок чипа. */
export const AI_EVIDENCE_ICONS: Readonly<Record<AiEvidenceKind, LucideIcon>> = {
  message: MessagesSquare,
  sms: MessageSquareText,
  note: StickyNote,
  task: ListChecks,
  attendance: CalendarCheck,
  mark: BookOpenCheck,
  survey: ClipboardList,
  agreement: FileText,
  payment: Wallet,
  invoice: Receipt,
  funding: UserRoundSearch,
};

/** Вид, которого экран ещё не знает, — нейтральное звено, а не пустота. */
export const AI_EVIDENCE_FALLBACK_ICON: LucideIcon = Link2;

export const aiEvidenceIconOf = (kind: string): LucideIcon =>
  (AI_EVIDENCE_ICONS as Record<string, LucideIcon>)[kind] ??
  AI_EVIDENCE_FALLBACK_ICON;
